import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from './client';

export interface BoardItem {
  id: string;
  url: string;
  title: string;
  description: string | null;
  sourceLabel: string;
  publishedAt: string | null;
  addedBy: string | null;
  addedAt: string;
  expiresAt: string | null;
}

export const EXPIRY_OPTIONS: { label: string; hours: number | null }[] = [
  { label: 'No expiry', hours: null },
  { label: '24 hours',  hours: 24 },
  { label: '3 days',    hours: 72 },
  { label: '7 days',    hours: 168 },
  { label: '14 days',   hours: 336 },
  { label: '30 days',   hours: 720 },
];

export const DEFAULT_EXPIRY_HOURS: number | null = 168;
export const STORAGE_EXPIRY_KEY = 'news-board-expiry-hours';

const BOARD_KEY = ['news-board'] as const;

export function useNewsBoard() {
  return useQuery({
    queryKey: BOARD_KEY,
    queryFn: () => api.get<{ items: BoardItem[] }>('/admin/news-board'),
    refetchInterval: 30_000,
    staleTime: 15_000,
  });
}

export function useAddBoardItem() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: {
      url: string;
      title: string;
      description: string | null;
      sourceLabel: string;
      publishedAt: string | null;
      expiresInHours: number | null;
    }) => api.post<BoardItem>('/admin/news-board', data),
    onSuccess: (item) => {
      qc.setQueryData<{ items: BoardItem[] }>(BOARD_KEY, (prev) => {
        if (!prev) return { items: [item] };
        const filtered = prev.items.filter((i) => i.url !== item.url);
        return { items: [item, ...filtered] };
      });
    },
  });
}

export function useRemoveBoardItem() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete(`/admin/news-board/${id}`),
    onMutate: async (id) => {
      await qc.cancelQueries({ queryKey: BOARD_KEY });
      const prev = qc.getQueryData<{ items: BoardItem[] }>(BOARD_KEY);
      qc.setQueryData<{ items: BoardItem[] }>(BOARD_KEY, (d) =>
        d ? { items: d.items.filter((i) => i.id !== id) } : d,
      );
      return { prev };
    },
    onError: (_e, _id, ctx) => {
      if (ctx?.prev) qc.setQueryData(BOARD_KEY, ctx.prev);
    },
    onSettled: () => qc.invalidateQueries({ queryKey: BOARD_KEY }),
  });
}

export function useClearBoard() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.delete('/admin/news-board'),
    onSuccess: () => qc.setQueryData<{ items: BoardItem[] }>(BOARD_KEY, { items: [] }),
  });
}

export function useUpdateBoardExpiry() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, expiresInHours }: { id: string; expiresInHours: number | null }) =>
      api.patch<BoardItem>(`/admin/news-board/${id}`, { expiresInHours }),
    onSuccess: (updated) => {
      qc.setQueryData<{ items: BoardItem[] }>(BOARD_KEY, (d) =>
        d ? { items: d.items.map((i) => (i.id === updated.id ? updated : i)) } : d,
      );
    },
  });
}
